const OPERATORS = {
  '<': (a, b) => a < b,
  '<=': (a, b) => a <= b,
  '>': (a, b) => a > b,
  '>=': (a, b) => a >= b,
  '==': (a, b) => a === b,
  '!=': (a, b) => a !== b,
};

const TEXT_OPERATORS = new Set(['==', '!=', 'in']);

const normalizeText = (value) => String(value).trim().toLowerCase();

export const getRangeBands = (range) => (Array.isArray(range) ? range : []);

const parseNumericResult = (rawResult) => {
  const trimmed = rawResult !== null && rawResult !== undefined ? String(rawResult).trim() : '';
  if (!trimmed) return null;
  const normalized = /^-?\d+,\d+$/.test(trimmed) ? trimmed.replace(',', '.') : trimmed;
  const value = parseFloat(normalized);
  return Number.isNaN(value) ? null : value;
};

const resolveAttribute = (attribute, patient) => {
  if (attribute === 'gender') {
    return patient?.gender ?? null;
  }
  if (attribute === 'age') {
    return patient?.age ?? null;
  }
  return null;
};

const conditionApplies = (condition, patient) => {
  const operator = OPERATORS[condition.operator];
  if (!operator) return false;
  const actual = resolveAttribute(condition.attribute, patient);
  if (actual === null) return false;
  const numericActual = Number(actual);
  const numericExpected = Number(condition.value);
  if (Number.isFinite(numericActual) && Number.isFinite(numericExpected)) {
    return operator(numericActual, numericExpected);
  }
  return operator(String(actual).trim().toLowerCase(), String(condition.value).trim().toLowerCase());
};

const criterionMatches = (criterion, textResult, numericResult) => {
  if (criterion.operator === 'in') {
    return Array.isArray(criterion.value)
      && criterion.value.some((option) => normalizeText(option) === normalizeText(textResult));
  }
  const operator = OPERATORS[criterion.operator];
  if (!operator) return false;
  const numericExpected = Number(criterion.value);
  if (numericResult !== null && Number.isFinite(numericExpected)) {
    return operator(numericResult, numericExpected);
  }
  if (criterion.operator === '==' || criterion.operator === '!=') {
    return operator(normalizeText(textResult), normalizeText(criterion.value));
  }
  return false;
};

const criteriaMatch = (criteria, textResult, numericResult) => criteria
  .every((criterion) => criterionMatches(criterion, textResult, numericResult));

export const evaluateRanges = (range, rawResult, patient) => {
  const evaluable = getRangeBands(range)
    .map((band, index) => ({ band, index }))
    .filter(({ band }) => Array.isArray(band.criteria) && band.criteria.length > 0);
  if (evaluable.length === 0) return null;

  const textResult = rawResult !== null && rawResult !== undefined ? String(rawResult).trim() : '';
  if (!textResult) return null;
  const numericResult = parseNumericResult(textResult);

  const applicable = evaluable
    .filter(({ band }) => (band.conditions || []).every((condition) => conditionApplies(condition, patient)));

  const candidates = numericResult === null
    ? applicable.filter(({ band }) => band.criteria.every((criterion) => TEXT_OPERATORS.has(criterion.operator)))
    : applicable;
  if (candidates.length === 0) return null;

  const hasNormalReference = candidates.some(({ band }) => band.isNormal === true);
  const matched = candidates.find(({ band }) => criteriaMatch(band.criteria, textResult, numericResult));
  if (!matched) {
    return hasNormalReference ? { label: null, isNormal: false, matchedIndex: null } : null;
  }
  const { band, index } = matched;
  if (band.isNormal === true) return { label: band.label ?? null, isNormal: true, matchedIndex: index };
  return { label: band.label ?? null, isNormal: hasNormalReference ? false : null, matchedIndex: index };
};
